<script setup>
import { Head, Link, usePage } from '@inertiajs/vue3';
import { onMounted, ref, computed } from 'vue';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';
import SocialShare from '@/Components/Front/SocialShare.vue';


const props = defineProps({
    setting: Object,
    mobil: Object,
    relatedMobils: Array,
    latestMobils: Array,
    popularJenis: Array,
    processedArtikel: String, // Pre-processed content from controller
    avgRating: [Number, String],
    reviewCount: [Number, String],
    // SEO props
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
    twitter_title: String,
    twitter_description: String,
    twitter_image: String,
    canonical_url: String,
});

// Format currency
const fmt = (value) => new Intl.NumberFormat('id-ID').format(value || 0);

// WhatsApp Link
const waNumber = computed(() => props.setting?.setting_no_wa || '6281234567890');
const bookingUrl = computed(() => {
    const text = encodeURIComponent(`Halo, saya ingin booking mobil ${props.mobil.mobil_nama}. Apakah masih tersedia?`);
    return `https://wa.me/${waNumber.value}?text=${text}`;
});

const reviewUrl = computed(() => {
    const text = encodeURIComponent(`Halo, saya ingin memberikan ulasan untuk ${props.mobil.mobil_nama}`);
    return `https://wa.me/${waNumber.value}?text=${text}`;
});

// Helper for currency
const formatCurrency = (value) => new Intl.NumberFormat('id-ID').format(value || 0);


// Breadcrumb Logic
const breadcrumbLabel = usePage().props.source_page === 'product' ? 'Mobil Favorit Pelanggan' : 'Daftar Armada';
const breadcrumbUrl = usePage().props.source_page === 'product' ? '/product' : '/daftar-armada';

// Helper for dates
const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(dateString));
};

// JSON-LD Schema
const jsonLd = computed(() => {
    const schema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": props.mobil.mobil_nama,
        "description": props.mobil.mobil_detail || `Rental mobil ${props.mobil.mobil_nama} di Banyuwangi.`,
        "image": props.mobil.mobil_foto ? `/storage/mobil/${props.mobil.mobil_foto}` : '/front/img/default-car.jpg',
        "brand": {
            "@type": "Brand",
            "name": props.setting?.setting_title || 'Gotransport Banyuwangi'
        },
        "category": props.mobil.jenis_mobil?.jenis_nama || 'Rental Mobil',
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": props.avgRating || 5,
            "reviewCount": props.reviewCount || 1,
            "bestRating": "5",
            "worstRating": "1"
        }
    };

    // Offers
    const offers = [];

    // Common fields for all offers to satisfy warnings
    const commonOfferFields = {
        "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "applicableCountry": "ID",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 1,
            "returnMethod": "https://schema.org/ReturnInStore",
            "returnFees": "https://schema.org/FreeReturn"
        },
        "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingRate": {
                "@type": "MonetaryAmount",
                "value": 0,
                "currency": "IDR"
            },
            "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "ID"
            },
            "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                    "@type": "QuantitativeValue",
                    "minValue": 0,
                    "maxValue": 1,
                    "unitCode": "DAY"
                },
                "transitTime": {
                    "@type": "QuantitativeValue",
                    "minValue": 0,
                    "maxValue": 1,
                    "unitCode": "DAY"
                }
            }
        }
    };

    if (props.mobil.mobil_harga_sewa) {
        offers.push({
            "@type": "Offer",
            "name": "Lepas Kunci",
            "price": props.mobil.mobil_harga_sewa,
            "priceCurrency": "IDR",
            "availability": "InStock",
            "seller": { "@type": "Organization", "name": props.setting?.setting_title || 'Gotransport' },
            ...commonOfferFields
        });
    }
    if (props.mobil.mobil_harga_include) {
        offers.push({
            "@type": "Offer",
            "name": "Include Driver",
            "price": props.mobil.mobil_harga_include,
            "priceCurrency": "IDR",
            "availability": "InStock",
            "seller": { "@type": "Organization", "name": props.setting?.setting_title || 'Gotransport' },
            ...commonOfferFields
        });
    }
    if (offers.length > 0) schema.offers = offers;

    return schema;
});
</script>

<template>
    <FrontLayout 
        :setting="setting"
        :title="title"
        :metaDescription="meta_description"
        :metaKeywords="meta_keywords"
        :ogTitle="og_title"
        :ogDescription="og_description"
        :ogImage="og_image"
        :ogUrl="og_url"
        :twitterTitle="twitter_title"
        :twitterDescription="twitter_description"
        :twitterImage="twitter_image"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <!-- Hero Section -->
        <PageHero 
            :title="mobil.mobil_nama"
            :subtitle="`Detail Armada ${mobil.jenis_mobil?.jenis_nama || 'Rental Mobil'}`"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: breadcrumbLabel, url: breadcrumbUrl },
                { label: mobil.mobil_nama }
            ]"
        />

        <section class="py-8 md:py-12 bg-white dark:bg-gray-900">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    <!-- Main Content -->
                    <div class="lg:col-span-2">
                        <!-- Primary Info Card -->
                        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm mb-8">
                            <div class="p-4">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <!-- Left: Image & Info -->
                                    <div>
                                        <div class="relative bg-white dark:bg-gray-700 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-600 mb-6 aspect-[4/3] flex items-center justify-center shadow-sm">
                                            <div v-if="mobil.jenis_mobil" class="absolute top-4 left-4 bg-blue-600 text-white px-4 py-1.5 text-xs font-bold rounded shadow-md z-10 uppercase tracking-wider">
                                                <i class="fa fa-tag mr-1"></i> {{ mobil.jenis_mobil.jenis_nama }}
                                            </div>
                                            <img v-if="mobil.mobil_foto" :src="`/storage/mobil/${mobil.mobil_foto}`" :alt="mobil.mobil_nama" class="max-w-full max-h-full object-contain p-6 hover:scale-105 transition-transform duration-500">
                                            <i v-else class="fa fa-car text-6xl text-gray-300"></i>
                                        </div>
                                        
                                        <div class="text-center px-4">
                                            <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-3">{{ mobil.mobil_nama }}</h2>
                                            <p v-if="mobil.mobil_detail" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed max-w-md mx-auto">{{ mobil.mobil_detail.replace(/<[^>]*>?/gm, '') }}</p>
                                        </div>
                                    </div>

                                    <!-- Right: Pricing & Actions -->
                                    <div class="flex flex-col justify-center space-y-8">
                                        <!-- Harga Sewa Section -->
                                        <div v-if="mobil.mobil_harga_sewa || mobil.mobil_harga_include">
                                            <h5 class="text-lg font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
                                                <i class="fa fa-money text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 p-1.5 rounded-md"></i> Harga Sewa
                                            </h5>
                                            <div class="space-y-4">
                                                <div v-if="mobil.mobil_harga_sewa" class="bg-blue-50 dark:bg-blue-900/20 border-l-[6px] border-blue-500 p-5 rounded-r-lg shadow-sm hover:shadow-md transition-shadow">
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex items-center gap-4">
                                                            <div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-blue-200 dark:shadow-none shadow-lg transform scale-100"><i class="fa fa-key text-sm"></i></div>
                                                            <span class="text-base font-medium text-gray-700 dark:text-gray-300">Lepas Kunci</span>
                                                        </div>
                                                        <div class="text-right">
                                                            <div class="text-2xl font-bold text-blue-700 dark:text-blue-400">Rp {{ fmt(mobil.mobil_harga_sewa) }}</div>
                                                            <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">/hari</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-if="mobil.mobil_harga_include" class="bg-green-50 dark:bg-green-900/20 border-l-[6px] border-green-500 p-5 rounded-r-lg shadow-sm hover:shadow-md transition-shadow">
                                                    <div class="flex items-center justify-between">
                                                        <div class="flex items-center gap-4">
                                                            <div class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-green-200 dark:shadow-none shadow-lg"><i class="fa fa-user text-sm"></i></div>
                                                            <span class="text-base font-medium text-gray-700 dark:text-gray-300">Include Driver</span>
                                                        </div>
                                                        <div class="text-right">
                                                            <div class="text-2xl font-bold text-green-700 dark:text-green-400">Rp {{ fmt(mobil.mobil_harga_include) }}</div>
                                                            <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">/hari</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Booking Section -->
                                        <div>
                                            <h5 class="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                                <i class="fa fa-phone text-green-600 dark:text-green-400"></i> Booking Sekarang
                                            </h5>
                                            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Hubungi kami untuk melakukan reservasi</p>
                                            
                                            <a :href="bookingUrl" target="_blank" class="block w-full text-center px-6 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold text-lg rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2">
                                                <i class="fa fa-whatsapp text-xl"></i> Booking Sekarang
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Specifications / Content -->
                        <div v-if="processedArtikel" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-8">
                             <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700 pb-4">
                                <i class="fa fa-file-text-o text-blue-600 dark:text-blue-400"></i> Spesifikasi & Detail
                             </h3>
                             <div class="prose dark:prose-invert max-w-none text-sm prose-blue prose-img:rounded-xl prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-a:text-blue-600 hover:prose-a:text-blue-700 transition-colors prose-p:my-1.5 prose-headings:my-3 prose-li:my-0 text-gray-700 dark:text-gray-300 leading-relaxed" v-html="processedArtikel"></div>
                        </div>

                        <!-- Social Share -->
                        <!-- Social Share -->
                        <div class="mb-8">
                            <SocialShare :url="canonical_url" :title="mobil.mobil_nama" />
                        </div>
                        


                    </div>

                    <!-- Sidebar -->
                    <aside class="lg:col-span-1 space-y-6">
                        <!-- Related Armada -->
                        <div v-if="relatedMobils && relatedMobils.length > 0">
                            <h4 class="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2 text-xl">
                                <i class="fa fa-car text-gray-700 dark:text-gray-400"></i> Armada Terkait
                            </h4>
                            <div class="space-y-6">
                                <div v-for="related in relatedMobils" :key="related.id" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
                                    <!-- Image Area -->
                                    <div class="relative bg-white dark:bg-gray-700 aspect-[4/3] border-b border-gray-100 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                                        <div v-if="related.jenis_mobil" class="absolute top-3 left-3 bg-blue-600 text-white text-[10px] uppercase font-bold px-3 py-1 rounded shadow-sm z-10">
                                            {{ related.jenis_mobil.jenis_nama }}
                                        </div>
                                        <img :src="related.mobil_foto ? `/storage/mobil/${related.mobil_foto}` : '/front/img/default-car.jpg'" :alt="related.mobil_nama" class="max-w-full max-h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500">
                                    </div>
                                    
                                    <div class="p-4">
                                        <!-- Title & Desc -->
                                        <h5 class="font-bold text-gray-900 dark:text-white text-lg mb-1 group-hover:text-blue-600 transition-colors">
                                            <Link :href="`/mobil/${related.mobil_slug}`">{{ related.mobil_nama }}</Link>
                                        </h5>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">
                                            {{ (related.mobil_detail || 'Cocok untuk perjalanan anda').replace(/<[^>]*>?/gm, '') }}
                                        </p>
                                        
                                        <!-- Pricing Rows -->
                                        <div class="space-y-2 mb-4">
                                            <div v-if="related.mobil_harga_sewa" class="flex items-center justify-between text-xs bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-2 rounded-r">
                                                <div class="flex items-center gap-2">
                                                    <div class="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white"><i class="fa fa-key text-[10px]"></i></div>
                                                    <span class="text-gray-700 dark:text-gray-300 font-medium">Lepas Kunci</span>
                                                </div>
                                                <span class="font-bold text-blue-700 dark:text-blue-400">Rp {{ fmt(related.mobil_harga_sewa) }}</span>
                                            </div>
                                            <div v-if="related.mobil_harga_include" class="flex items-center justify-between text-xs bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-2 rounded-r">
                                                <div class="flex items-center gap-2">
                                                    <div class="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white"><i class="fa fa-user text-[10px]"></i></div>
                                                    <span class="text-gray-700 dark:text-gray-300 font-medium">Include Driver</span>
                                                </div>
                                                <span class="font-bold text-green-700 dark:text-green-400">Rp {{ fmt(related.mobil_harga_include) }}</span>
                                            </div>
                                        </div>
                                        
                                        <!-- Booking Button -->
                                        <a :href="`https://wa.me/${waNumber}?text=Info%20booking%20${related.mobil_nama}`" target="_blank" class="block w-full text-center bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-bold py-2.5 rounded transition-all shadow-md hover:shadow-lg uppercase tracking-wide">
                                            <i class="fa fa-whatsapp mr-1"></i> BOOKING
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Categories Widget -->
                        <div v-if="popularJenis && popularJenis.length > 0" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm">
                            <h4 class="font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2 text-xl">
                                <i class="fa fa-tags text-gray-800 dark:text-gray-300"></i> Kategori Armada
                            </h4>
                            <div class="flex flex-col">
                                <Link v-for="jenis in popularJenis" :key="jenis.id" :href="`/mobil/jenis/${jenis.jenis_slug}`" class="flex items-center justify-between py-3.5 border-b border-gray-100 dark:border-gray-700 last:border-0 group hover:pl-2 transition-all duration-300">
                                    <div class="flex items-center gap-3">
                                        <i class="fa fa-car text-gray-400 group-hover:text-blue-600 transition-colors"></i>
                                        <span class="text-gray-600 dark:text-gray-300 group-hover:text-blue-600 font-medium">{{ jenis.jenis_nama }}</span>
                                    </div>
                                    <span class="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs py-1 px-2.5 rounded group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors font-medium">{{ jenis.mobils_count }}</span>
                                </Link>
                            </div>
                        </div>



                    </aside>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
